<script setup>
import { onMounted, onBeforeUnmount, computed, reactive } from "vue";
import { useAuthStore } from "@/stores/authStore";
import { useCompanyStore } from "@/stores/companyStore";
import { usePayrollConfigStore } from "@/stores/payrollConfigStore";
import { useDepartmentStore } from "@/stores/departmentStore";
import { useEmployeeStore } from "@/stores/employeeStore";
import { usePayrollOvertimeStore } from "@/stores/payrollOvertimeStore";
import { storeToRefs } from "pinia";

import layoutHeader from "@/views/layouts/layout-header.vue";
import layoutSidebar from "@/views/layouts/layout-sidebar.vue";


const title = "Payroll Settings";
const text = "Payroll Company Settings";
const text1 = "Settings";

// ---------- Auth / role ----------
const authStore = useAuthStore();
const { user } = storeToRefs(authStore);

const isSuperAdmin = computed(() => {
  const roles = (user.value?.roles || []).map((r) =>
    typeof r === "string" ? r : r.name
  );
  return roles.includes("SuperAdmin");
});

// ---------- Company ----------
const companyStore = useCompanyStore();
const { companies, activeCompanyId } = storeToRefs(companyStore);

const activeCompany = computed(() =>
  companies.value.find((c) => c.id === activeCompanyId.value)
);

const companyCountry = computed(
  () =>
    activeCompany.value?.country_code ||
    "KE" // fallback
);

const companyCurrency = computed(
  () =>
    activeCompany.value?.currency_code ||
    "KES" // fallback
);

// ---------- Payroll config store ----------
const payrollConfigStore = usePayrollConfigStore();
const loading = computed(() => payrollConfigStore.loading);
const departmentStore = useDepartmentStore();
const employeeStore = useEmployeeStore();
const payrollOvertimeStore = usePayrollOvertimeStore();
const { departments } = storeToRefs(departmentStore);
const { employees } = storeToRefs(employeeStore);
const { overtimeList } = storeToRefs(payrollOvertimeStore);

const overtimeTypeOptions = computed(() =>
  (overtimeList.value || []).map((item) => ({
    id: Number(item.id),
    label: `${item.name} (${item.rate_type || "rate"} ${item.rate}x)`,
  }))
);

// ---------- Form state ----------
const form = reactive({
  country: "KE",

  nssf_lower_limit: 8000,
  nssf_upper_limit: 72000,
  nssf_rate: 0.06,
  nssf_employee_cap: 4320,
  nssf_employer_match: true,

  shif_rate: 0.0275,
  shif_min: 300,

  ahl_rate: 0.015,

  personal_relief: 2400,
  insurance_relief_rate: 0.15,
  insurance_relief_cap: 5000,

  paye_bands: [
    { up_to: 24000, rate: 0.1 },
    { up_to: 32333, rate: 0.25 },
    { up_to: 500000, rate: 0.3 },
    { up_to: 800000, rate: 0.325 },
  ],
  paye_top_rate: 0.35,

  attendance_deduct_absent_days: false,
  attendance_deduct_unpaid_leave_days: false,
  attendance_salary_basis: "basic_salary",
  attendance_monthly_working_days: 22,
  attendance_auto_apply_overtime: true,
  attendance_default_overtime_id: "",
  attendance_default_rate_multiplier: 1.5,
  attendance_skip_auto_when_manual_entry: true,

  overtime_monthly_hours: 160,

  timesheet_deduct_unworked_hours: false,
  timesheet_salary_basis: "basic_salary",
  timesheet_department_ids: [],
  timesheet_employee_ids: [],
  approval_require_separate_approver: true,

  advance_calculation_mode: "fixed",
  advance_default_percent_of_basic: 50,

  net_pay_rounding_mode: "none",

  payslip_orientation: "portrait",
});

const ui = reactive({
  timesheetDepartmentQuery: "",
  timesheetEmployeeQuery: "",
});

const departmentOptions = computed(() =>
  (departments.value || []).map((d) => ({
    id: Number(d.id),
    name: d.name || `Department #${d.id}`,
  }))
);

const filteredDepartmentOptions = computed(() => {
  const q = String(ui.timesheetDepartmentQuery || "").trim().toLowerCase();
  if (!q) return departmentOptions.value;
  return departmentOptions.value.filter((dept) =>
    String(dept.name || "").toLowerCase().includes(q)
  );
});

const selectedTimesheetDepartments = computed(() =>
  departmentOptions.value.filter((dept) =>
    (form.timesheet_department_ids || []).includes(dept.id)
  )
);

const employeeOptions = computed(() =>
  (employees.value || [])
    .filter(
      (e) => String(e?.status || "").trim().toLowerCase() === "active",
    )
    .map((e) => ({
      id: Number(e.id),
      name:
        [e.first_name, e.last_name].filter(Boolean).join(" ").trim() ||
        e.employee_id ||
        `Employee #${e.id}`,
      code: e.employee_id || "",
    })),
);

const filteredEmployeeOptions = computed(() => {
  const q = String(ui.timesheetEmployeeQuery || "").trim().toLowerCase();
  if (!q) return employeeOptions.value;
  return employeeOptions.value.filter((emp) => {
    const name = String(emp.name || "").toLowerCase();
    const code = String(emp.code || "").toLowerCase();
    return name.includes(q) || code.includes(q);
  });
});

const selectedTimesheetEmployees = computed(() =>
  employeeOptions.value.filter((emp) =>
    (form.timesheet_employee_ids || []).includes(emp.id)
  )
);

function toggleTimesheetDepartment(id) {
  const next = new Set((form.timesheet_department_ids || []).map(Number));
  if (next.has(Number(id))) next.delete(Number(id));
  else next.add(Number(id));
  form.timesheet_department_ids = Array.from(next);
}

function clearTimesheetDepartments() {
  form.timesheet_department_ids = [];
  ui.timesheetDepartmentQuery = "";
}

function removeTimesheetDepartment(id) {
  const target = Number(id);
  form.timesheet_department_ids = (form.timesheet_department_ids || [])
    .map(Number)
    .filter((value) => value !== target);
}

function toggleTimesheetEmployee(id) {
  const next = new Set((form.timesheet_employee_ids || []).map(Number));
  if (next.has(Number(id))) next.delete(Number(id));
  else next.add(Number(id));
  form.timesheet_employee_ids = Array.from(next);
}

function clearTimesheetEmployees() {
  form.timesheet_employee_ids = [];
  ui.timesheetEmployeeQuery = "";
}

function removeTimesheetEmployee(id) {
  const target = Number(id);
  form.timesheet_employee_ids = (form.timesheet_employee_ids || [])
    .map(Number)
    .filter((value) => value !== target);
}

function cleanupPageScrollLocks() {
  if (typeof document === "undefined") return;

  document.body.classList.remove("modal-open");
  document.body.style.removeProperty("overflow");
  document.body.style.removeProperty("padding-right");
  document.body.style.removeProperty("touch-action");

  document.documentElement.style.removeProperty("overflow");
  document.documentElement.style.removeProperty("padding-right");
  document.documentElement.style.removeProperty("touch-action");

  document
    .querySelectorAll(".modal-backdrop, .offcanvas-backdrop")
    .forEach((backdrop) => backdrop.remove());
}

// Country list (used only for SuperAdmin preset UI)
const countryOptions = [
  { code: "KE", name: "Kenya" },
  { code: "UG", name: "Uganda" },
  { code: "TZ", name: "Tanzania" },
  { code: "RW", name: "Rwanda" },
  { code: "BI", name: "Burundi" },
  { code: "SS", name: "South Sudan" },
  { code: "ET", name: "Ethiopia" },
];

// ---- Preset definitions (non-Kenya values are placeholders; can be refined) ----
const presets = {
  KE: {
    country: "KE",
    nssf_lower_limit: 8000,
    nssf_upper_limit: 72000,
    nssf_rate: 0.06,
    nssf_employee_cap: 4320,
    nssf_employer_match: true,
    shif_rate: 0.0275,
    shif_min: 300,
    ahl_rate: 0.015,
    personal_relief: 2400,
    insurance_relief_rate: 0.15,
    insurance_relief_cap: 5000,
    paye_bands: [
      { up_to: 24000, rate: 0.1 },
      { up_to: 32333, rate: 0.25 },
      { up_to: 500000, rate: 0.3 },
      { up_to: 800000, rate: 0.325 },
    ],
    paye_top_rate: 0.35,
  },

  // NOTE: these are example bands; refine per actual country rules
  UG: {
    country: "UG",
    nssf_lower_limit: 0,
    nssf_upper_limit: 0,
    nssf_rate: 0.05,
    nssf_employee_cap: 0,
    nssf_employer_match: false,
    shif_rate: 0,
    shif_min: 0,
    ahl_rate: 0,
    personal_relief: 0,
    insurance_relief_rate: 0,
    insurance_relief_cap: 0,
    paye_bands: [
      { up_to: 235000, rate: 0.1 },
      { up_to: 335000, rate: 0.2 },
      { up_to: 410000, rate: 0.3 },
      { up_to: 999999999, rate: 0.4 },
    ],
    paye_top_rate: 0.4,
  },

  TZ: {
    country: "TZ",
    nssf_lower_limit: 0,
    nssf_upper_limit: 0,
    nssf_rate: 0.1,
    nssf_employee_cap: 0,
    nssf_employer_match: false,
    shif_rate: 0,
    shif_min: 0,
    ahl_rate: 0,
    personal_relief: 0,
    insurance_relief_rate: 0,
    insurance_relief_cap: 0,
    paye_bands: [
      { up_to: 270000, rate: 0.0 },
      { up_to: 520000, rate: 0.09 },
      { up_to: 760000, rate: 0.2 },
      { up_to: 1000000, rate: 0.25 },
    ],
    paye_top_rate: 0.3,
  },

  RW: {
    country: "RW",
    nssf_lower_limit: 0,
    nssf_upper_limit: 0,
    nssf_rate: 0.05,
    nssf_employee_cap: 0,
    nssf_employer_match: false,
    shif_rate: 0,
    shif_min: 0,
    ahl_rate: 0,
    personal_relief: 0,
    insurance_relief_rate: 0,
    insurance_relief_cap: 0,
    paye_bands: [
      { up_to: 30000, rate: 0.0 },
      { up_to: 100000, rate: 0.2 },
      { up_to: 999999999, rate: 0.3 },
    ],
    paye_top_rate: 0.3,
  },
};

// Apply preset (only visible/used by SuperAdmin)
function applyPreset(code) {
  const preset = presets[code];
  if (!preset) return;

  form.country = preset.country;

  form.nssf_lower_limit = preset.nssf_lower_limit;
  form.nssf_upper_limit = preset.nssf_upper_limit;
  form.nssf_rate = preset.nssf_rate;
  form.nssf_employee_cap = preset.nssf_employee_cap;
  form.nssf_employer_match = preset.nssf_employer_match;

  form.shif_rate = preset.shif_rate;
  form.shif_min = preset.shif_min;

  form.ahl_rate = preset.ahl_rate;

  form.personal_relief = preset.personal_relief;
  form.insurance_relief_rate = preset.insurance_relief_rate;
  form.insurance_relief_cap = preset.insurance_relief_cap;

  form.paye_bands = preset.paye_bands.map((b) => ({
    up_to: b.up_to,
    rate: b.rate,
  }));
  form.paye_top_rate = preset.paye_top_rate;
}

// Load config on mount
onMounted(async () => {
  cleanupPageScrollLocks();

  if (!companies.value.length) {
    await companyStore.fetchCompanies?.();
  }

  await Promise.all([
    payrollConfigStore.fetchConfig(),
    departmentStore.fetchDepartments?.({ per_page: 5000 }),
    employeeStore.fetchEmployees?.({ status: "Active", per_page: 5000 }),
    payrollOvertimeStore.fetchAll(),
  ]);
  const cfg = payrollConfigStore.config || {};

  // country: SuperAdmin can truly decide; non-super uses companyCountry
  form.country = cfg.country || companyCountry.value || "KE";

  const nssf = cfg.nssf || {};
  const shif = cfg.shif || {};
  const ahl = cfg.ahl || {};
  const paye = cfg.paye || {};

  form.nssf_lower_limit = nssf.lower_limit ?? 8000;
  form.nssf_upper_limit = nssf.upper_limit ?? 72000;
  form.nssf_rate = nssf.rate ?? 0.06;
  form.nssf_employee_cap = nssf.employee_cap ?? 4320;
  form.nssf_employer_match =
    typeof nssf.employer_match === "boolean" ? nssf.employer_match : true;

  form.shif_rate = shif.rate ?? 0.0275;
  form.shif_min = shif.min ?? 300;

  form.ahl_rate = ahl.rate ?? 0.015;

  form.personal_relief = paye.personal_relief ?? 2400;
  form.insurance_relief_rate = paye.insurance_relief_rate ?? 0.15;
  form.insurance_relief_cap = paye.insurance_relief_cap ?? 5000;

  const bands =
    Array.isArray(paye.bands) && paye.bands.length
      ? paye.bands
      : [
          { up_to: 24000, rate: 0.1 },
          { up_to: 32333, rate: 0.25 },
          { up_to: 500000, rate: 0.3 },
          { up_to: 800000, rate: 0.325 },
        ];

  form.paye_bands = [
    { up_to: bands[0]?.up_to ?? 24000, rate: bands[0]?.rate ?? 0.1 },
    { up_to: bands[1]?.up_to ?? 32333, rate: bands[1]?.rate ?? 0.25 },
    { up_to: bands[2]?.up_to ?? 500000, rate: bands[2]?.rate ?? 0.3 },
    { up_to: bands[3]?.up_to ?? 800000, rate: bands[3]?.rate ?? 0.325 },
  ];

  form.paye_top_rate = paye.top_rate ?? 0.35;

  const attendancePayroll = cfg.attendance_payroll || {};
  form.attendance_deduct_absent_days =
    !!attendancePayroll.deduct_absent_days;
  form.attendance_deduct_unpaid_leave_days =
    !!attendancePayroll.deduct_unpaid_leave_days;
  form.attendance_salary_basis =
    attendancePayroll.salary_basis || "basic_salary";
  form.attendance_monthly_working_days =
    attendancePayroll.monthly_working_days ?? 22;
  form.attendance_auto_apply_overtime =
    typeof attendancePayroll.auto_apply_overtime === "boolean"
      ? attendancePayroll.auto_apply_overtime
      : true;
  form.attendance_default_overtime_id =
    attendancePayroll.default_overtime_id != null
      ? String(attendancePayroll.default_overtime_id)
      : "";
  form.attendance_default_rate_multiplier =
    attendancePayroll.default_rate_multiplier ?? 1.5;
  form.attendance_skip_auto_when_manual_entry =
    typeof attendancePayroll.skip_auto_when_manual_entry === "boolean"
      ? attendancePayroll.skip_auto_when_manual_entry
      : true;

  const overtimeRules = cfg.overtime || {};
  form.overtime_monthly_hours = overtimeRules.monthly_hours ?? 160;

  const timesheetPayroll = cfg.timesheet_payroll || {};
  form.timesheet_deduct_unworked_hours =
    !!timesheetPayroll.deduct_unworked_hours;
  form.timesheet_salary_basis =
    timesheetPayroll.salary_basis || "basic_salary";
  form.timesheet_department_ids = Array.isArray(timesheetPayroll.department_ids)
    ? timesheetPayroll.department_ids.map((id) => Number(id)).filter(Boolean)
    : [];
  form.timesheet_employee_ids = Array.isArray(timesheetPayroll.employee_ids)
    ? timesheetPayroll.employee_ids.map((id) => Number(id)).filter(Boolean)
    : [];

  const approvalWorkflow = cfg.approval_workflow || {};
  form.approval_require_separate_approver =
    typeof approvalWorkflow.require_separate_approver === "boolean"
      ? approvalWorkflow.require_separate_approver
      : true;

  const advance = cfg.advance || {};
  form.advance_calculation_mode = advance.calculation_mode || "fixed";
  form.advance_default_percent_of_basic =
    advance.default_percent_of_basic ?? 50;

  const netPayRounding = cfg.net_pay_rounding || {};
  form.net_pay_rounding_mode =
    netPayRounding.mode === "nearest_shilling" ? "nearest_shilling" : "none";

  const payslip = cfg.payslip || {};
  form.payslip_orientation =
    payslip.orientation === "landscape" ? "landscape" : "portrait";
});

onBeforeUnmount(() => {
  cleanupPageScrollLocks();
});

async function handleSave() {
  // Force country from company for non-super-admins
  const countryToSave = isSuperAdmin.value
    ? form.country
    : companyCountry.value || form.country || "KE";

  const payload = {
    country: countryToSave,

    nssf: {
      lower_limit: Number(form.nssf_lower_limit),
      upper_limit: Number(form.nssf_upper_limit),
      rate: Number(form.nssf_rate),
      employee_cap: Number(form.nssf_employee_cap),
      employer_match: !!form.nssf_employer_match,
    },

    shif: {
      rate: Number(form.shif_rate),
      min: Number(form.shif_min),
    },

    ahl: {
      rate: Number(form.ahl_rate),
    },

    paye: {
      personal_relief: Number(form.personal_relief),
      insurance_relief_rate: Number(form.insurance_relief_rate),
      insurance_relief_cap: Number(form.insurance_relief_cap),
      bands: form.paye_bands
        .filter((b) => b.up_to && b.rate)
        .map((b) => ({
          up_to: Number(b.up_to),
          rate: Number(b.rate),
        })),
      top_rate: Number(form.paye_top_rate),
    },

    attendance_payroll: {
      deduct_absent_days: !!form.attendance_deduct_absent_days,
      deduct_unpaid_leave_days: !!form.attendance_deduct_unpaid_leave_days,
      salary_basis: form.attendance_salary_basis || "basic_salary",
      monthly_working_days: Number(form.attendance_monthly_working_days || 22),
      auto_apply_overtime: !!form.attendance_auto_apply_overtime,
      default_overtime_id: form.attendance_default_overtime_id
        ? Number(form.attendance_default_overtime_id)
        : null,
      default_rate_multiplier: Number(
        form.attendance_default_rate_multiplier || 1.5
      ),
      skip_auto_when_manual_entry:
        !!form.attendance_skip_auto_when_manual_entry,
    },

    overtime: {
      monthly_hours: Math.min(
        400,
        Math.max(1, Number(form.overtime_monthly_hours || 160)),
      ),
    },

    timesheet_payroll: {
      deduct_unworked_hours: !!form.timesheet_deduct_unworked_hours,
      salary_basis: form.timesheet_salary_basis || "basic_salary",
      approved_only: true,
      department_ids: (form.timesheet_department_ids || []).map((id) => Number(id)).filter(Boolean),
      employee_ids: (form.timesheet_employee_ids || []).map((id) => Number(id)).filter(Boolean),
    },

    approval_workflow: {
      require_separate_approver: !!form.approval_require_separate_approver,
    },

    advance: {
      calculation_mode: form.advance_calculation_mode || "fixed",
      default_percent_of_basic: Math.min(
        100,
        Math.max(0, Number(form.advance_default_percent_of_basic ?? 50)),
      ),
    },

    net_pay_rounding: {
      mode: form.net_pay_rounding_mode === "nearest_shilling" ? "nearest_shilling" : "none",
    },

    payslip: {
      orientation: form.payslip_orientation === "landscape" ? "landscape" : "portrait",
    },
  };

  await payrollConfigStore.saveConfig(payload);
}
</script>

<template>
  <layout-header></layout-header>
  <layout-sidebar></layout-sidebar>

  <div class="page-wrapper dq-payroll-settings">
    <div class="content">
      <!-- Page header -->
      <div
        class="d-md-flex d-block align-items-center justify-content-between page-breadcrumb mb-3"
      >
        <breadcrumb :title="title" :text="text" :text1="text1" />

        <!-- Quick actions (UX only) -->
        <div class="d-flex align-items-center gap-2 mt-3 mt-md-0">
          <span class="dq-pill">
            <i class="ti ti-shield-check me-1"></i>
            Statutory setup
          </span>
          <span v-if="isSuperAdmin" class="dq-pill dq-pill-soft">
            <i class="ti ti-crown me-1"></i>
            SuperAdmin
          </span>
        </div>
      </div>

      <!-- Company context hero -->
      <div class="dq-hero card mb-3">
        <div class="card-body">
          <div class="dq-hero-grid">
            <div class="dq-hero-left">
              <div class="dq-hero-badge">
                <i class="ti ti-settings"></i>
              </div>
              <div>
                <h4 class="dq-hero-title mb-1">Payroll Settings</h4>
                <p class="dq-hero-sub mb-0">
                  Configure statutory rates and thresholds for accurate payroll deductions and PAYE.
                </p>
              </div>
            </div>

            <div class="dq-hero-right">
              <div class="dq-meta">
                <div class="dq-meta-label">Company</div>
                <div class="dq-meta-value text-truncate">
                  {{ activeCompany?.company_name || "Current Company" }}
                </div>
              </div>

              <div class="dq-meta">
                <div class="dq-meta-label">Country</div>
                <div class="dq-meta-value">
                  {{ companyCountry }}
                </div>
              </div>

              <div class="dq-meta">
                <div class="dq-meta-label">Currency</div>
                <div class="dq-meta-value">
                  {{ companyCurrency }}
                </div>
              </div>
            </div>
          </div>

          <div class="dq-hero-hint mt-3">
            <i class="ti ti-info-circle me-2"></i>
            Tip: Rates are stored per company. Review changes carefully before saving.
          </div>
        </div>
      </div>

      <!-- Main config card -->
      <div class="card dq-card">
        <div class="card-header dq-card-header">
          <div class="d-flex align-items-start justify-content-between gap-3 flex-wrap">
            <div class="d-flex align-items-center gap-3">
              <span class="dq-icon-badge dq-icon-blue">
                <i class="ti ti-adjustments"></i>
              </span>
              <div>
                <h5 class="mb-1">Payroll Configuration</h5>
                <small class="text-muted d-block">
                  Set statutory thresholds and reliefs. Use presets  as a starting point.
                </small>
              </div>
            </div>

            <div class="dq-status" v-if="loading">
              <span class="spinner-border spinner-border-sm me-2"></span>
              Saving…
            </div>
          </div>
        </div>

        <div class="card-body dq-card-body">
          <form @submit.prevent="handleSave">
            <!-- Country & Presets -->
            <div class="dq-section">
              <div class="dq-section-head">
                <div class="dq-section-title">
                  <span class="dq-section-icon dq-icon-purple">
                    <i class="ti ti-world"></i>
                  </span>
                  <div>
                    <div class="dq-h6">Country & Presets</div>
                    <div class="dq-help">
                      Tax regime, company and country .
                    </div>
                  </div>
                </div>
              </div>

              <div class="dq-section-body">
                <div class="row g-3">
                  <template v-if="isSuperAdmin">
                    <div class="col-md-6">
                      <label class="form-label dq-label">Country / Tax Regime</label>
                      <select v-model="form.country" class="form-select dq-input">
                        <option v-for="c in countryOptions" :key="c.code" :value="c.code">
                          {{ c.name }} ({{ c.code }})
                        </option>
                      </select>
                      <div class="dq-help mt-1">
                        This is informational. Actual computations use the figures below.
                      </div>
                    </div>

                    <div class="col-md-6">
                      <label class="form-label dq-label">Apply Preset</label>

                      <div class="dropdown w-100">
                        <button
                          type="button"
                          class="btn btn-outline-secondary dq-btn-outline w-100 d-flex align-items-center justify-content-between"
                          data-bs-toggle="dropdown"
                        >
                          <span class="text-truncate">
                            Choose Country Preset
                          </span>
                          <i class="ti ti-chevron-down"></i>
                        </button>

                        <ul class="dropdown-menu w-100 dq-dropdown">
                          <li v-for="c in countryOptions" :key="c.code">
                            <a
                              href="javascript:void(0);"
                              class="dropdown-item dq-dropdown-item"
                              @click.prevent="applyPreset(c.code)"
                            >
                              <span class="me-2 dq-dot"></span>
                              {{ c.name }} Preset
                            </a>
                          </li>
                        </ul>
                      </div>

                      <div class="dq-help mt-1">
                        Use a preset as a starting point, then adjust if needed.
                      </div>
                    </div>
                  </template>

                  <template v-else>
                    <div class="col-md-6">
                      <label class="form-label dq-label">Country / Tax Regime</label>
                      <input
                        type="text"
                        class="form-control dq-input"
                        :value="companyCountry"
                        disabled
                      />
                      <div class="dq-help mt-1">
                        Country is based on your registered company.
                      </div>
                    </div>
                  </template>
                </div>
              </div>
            </div>

            <!-- NSSF -->
            <div class="dq-section">
              <div class="dq-section-head">
                <div class="dq-section-title">
                  <span class="dq-section-icon dq-icon-green">
                    <i class="ti ti-pig-money"></i>
                  </span>
                  <div>
                    <div class="dq-h6">NSSF / Pension</div>
                    <div class="dq-help">
                      Configure Tier limits, rate and employee cap.
                    </div>
                  </div>
                </div>
              </div>

              <div class="dq-section-body">
                <div class="row g-3">
                  <div class="col-md-3">
                    <label class="form-label dq-label">Lower Limit (Tier I)</label>
                    <input v-model.number="form.nssf_lower_limit" type="number" min="0" class="form-control dq-input" required />
                  </div>

                  <div class="col-md-3">
                    <label class="form-label dq-label">Upper Limit (Tier II)</label>
                    <input v-model.number="form.nssf_upper_limit" type="number" min="0" class="form-control dq-input" required />
                  </div>

                  <div class="col-md-3">
                    <label class="form-label dq-label">Contribution Rate</label>
                    <div class="input-group dq-input-group">
                      <input v-model.number="form.nssf_rate" type="number" step="0.0001" min="0" class="form-control dq-input" required />
                      <span class="input-group-text dq-addon">0.06 = 6%</span>
                    </div>
                  </div>

                  <div class="col-md-3">
                    <label class="form-label dq-label">Employee Cap</label>
                    <input v-model.number="form.nssf_employee_cap" type="number" min="0" class="form-control dq-input" required />
                  </div>

                  <div class="col-md-4">
                    <div class="dq-switch-card">
                      <div class="dq-switch-left">
                        <div class="dq-switch-title">Employer matches NSSF</div>
                        <div class="dq-help mb-0">If enabled, employer contribution equals employee.</div>
                      </div>
                      <div class="form-check form-switch m-0">
                        <input
                          class="form-check-input dq-switch"
                          type="checkbox"
                          id="nssf_employer_match"
                          v-model="form.nssf_employer_match"
                        />
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            </div>

            <!-- SHIF -->
            <div class="dq-section">
              <div class="dq-section-head">
                <div class="dq-section-title">
                  <span class="dq-section-icon dq-icon-blue">
                    <i class="ti ti-heartbeat"></i>
                  </span>
                  <div>
                    <div class="dq-h6">SHIF / NHIF</div>
                    <div class="dq-help">
                      Health insurance contribution settings.
                    </div>
                  </div>
                </div>
              </div>

              <div class="dq-section-body">
                <div class="row g-3">
                  <div class="col-md-4">
                    <label class="form-label dq-label">Rate</label>
                    <div class="input-group dq-input-group">
                      <input v-model.number="form.shif_rate" type="number" step="0.0001" min="0" class="form-control dq-input" required />
                      <span class="input-group-text dq-addon">0.0275 = 2.75%</span>
                    </div>
                  </div>

                  <div class="col-md-4">
                    <label class="form-label dq-label">Minimum Contribution</label>
                    <input v-model.number="form.shif_min" type="number" min="0" class="form-control dq-input" required />
                  </div>
                </div>
              </div>
            </div>

            <!-- AHL -->
            <div class="dq-section">
              <div class="dq-section-head">
                <div class="dq-section-title">
                  <span class="dq-section-icon dq-icon-warn">
                    <i class="ti ti-home"></i>
                  </span>
                  <div>
                    <div class="dq-h6">Affordable Housing Levy</div>
                    <div class="dq-help">
                      Employee levy rate (where applicable).
                    </div>
                  </div>
                </div>
              </div>

              <div class="dq-section-body">
                <div class="row g-3">
                  <div class="col-md-4">
                    <label class="form-label dq-label">Rate</label>
                    <div class="input-group dq-input-group">
                      <input v-model.number="form.ahl_rate" type="number" step="0.0001" min="0" class="form-control dq-input" required />
                      <span class="input-group-text dq-addon">0.015 = 1.5%</span>
                    </div>
                  </div>
                </div>
              </div>
            </div>

            <!-- PAYE Reliefs -->
            <div class="dq-section">
              <div class="dq-section-head">
                <div class="dq-section-title">
                  <span class="dq-section-icon dq-icon-purple">
                    <i class="ti ti-receipt-tax"></i>
                  </span>
                  <div>
                    <div class="dq-h6">PAYE Reliefs</div>
                    <div class="dq-help">
                      Configure relief values applied during PAYE computation.
                    </div>
                  </div>
                </div>
              </div>

              <div class="dq-section-body">
                <div class="row g-3">
                  <div class="col-md-4">
                    <label class="form-label dq-label">Personal Relief</label>
                    <input v-model.number="form.personal_relief" type="number" min="0" class="form-control dq-input" required />
                  </div>

                  <div class="col-md-4">
                    <label class="form-label dq-label">Insurance Relief Rate</label>
                    <div class="input-group dq-input-group">
                      <input v-model.number="form.insurance_relief_rate" type="number" step="0.0001" min="0" class="form-control dq-input" required />
                      <span class="input-group-text dq-addon">0.15 = 15%</span>
                    </div>
                  </div>

                  <div class="col-md-4">
                    <label class="form-label dq-label">Insurance Relief Cap</label>
                    <input v-model.number="form.insurance_relief_cap" type="number" min="0" class="form-control dq-input" required />
                  </div>
                </div>
              </div>
            </div>

            <div class="dq-section">
              <div class="dq-section-head">
                <div class="dq-section-title">
                  <span class="dq-section-icon dq-icon-green">
                    <i class="ti ti-calendar-due"></i>
                  </span>
                  <div>
                    <div class="dq-h6">Attendance & Leave Impact</div>
                    <div class="dq-help">
                      Optional payroll deductions from attendance and unpaid leave. Disabled by default.
                    </div>
                  </div>
                </div>
              </div>

              <div class="dq-section-body">
                <div class="alert alert-warning mb-3">
                  These controls are optional. Payroll ignores attendance and leave unless you enable the toggles below.
                </div>

                <div class="row g-3">
                  <div class="col-md-6">
                    <div class="dq-switch-card h-100">
                      <div class="dq-switch-left">
                        <div class="dq-switch-title">Deduct Absent Days</div>
                        <div class="dq-help mb-0">
                          Uses attendance records with status <code>Absent</code> in the payroll month.
                          Pending or unapproved attendance is ignored.
                        </div>
                      </div>
                      <div class="form-check form-switch m-0">
                        <input
                          class="form-check-input dq-switch"
                          type="checkbox"
                          id="attendance_deduct_absent_days"
                          v-model="form.attendance_deduct_absent_days"
                        />
                      </div>
                    </div>
                  </div>

                  <div class="col-md-6">
                    <div class="dq-switch-card h-100">
                      <div class="dq-switch-left">
                        <div class="dq-switch-title">Deduct Unpaid Leave Days</div>
                        <div class="dq-help mb-0">
                          Uses <code>Approved</code> leave whose leave type is marked unpaid.
                        </div>
                      </div>
                      <div class="form-check form-switch m-0">
                        <input
                          class="form-check-input dq-switch"
                          type="checkbox"
                          id="attendance_deduct_unpaid_leave_days"
                          v-model="form.attendance_deduct_unpaid_leave_days"
                        />
                      </div>
                    </div>
                  </div>

                  <div class="col-md-4">
                    <label class="form-label dq-label">Salary Basis</label>
                    <select v-model="form.attendance_salary_basis" class="form-select dq-input">
                      <option value="basic_salary">Basic Salary</option>
                      <option value="gross_salary">Gross Salary</option>
                    </select>
                    <div class="dq-help mt-1">
                      Per-day deduction basis when one of the toggles is enabled.
                    </div>
                  </div>

                  <div class="col-md-4">
                    <label class="form-label dq-label">Monthly Working Days</label>
                    <input
                      v-model.number="form.attendance_monthly_working_days"
                      type="number"
                      min="1"
                      class="form-control dq-input"
                    />
                    <div class="dq-help mt-1">
                      Divisor used to convert salary basis into a daily rate.
                    </div>
                  </div>

                  <div class="col-md-4">
                    <label class="form-label dq-label">Monthly hours (OT hourly rate)</label>
                    <input
                      v-model.number="form.overtime_monthly_hours"
                      type="number"
                      min="1"
                      max="400"
                      step="1"
                      class="form-control dq-input"
                    />
                    <div class="dq-help mt-1">
                      Converts basic salary to an hourly rate for manual and imported overtime:
                      <code>basic ÷ hours × OT hours × multiplier</code>. Use
                      <strong>218</strong> if that matches your spreadsheet (default is 160).
                    </div>
                  </div>

                  <div class="col-12">
                    <hr class="my-1" />
                  </div>

                  <div class="col-md-6">
                    <div class="dq-switch-card h-100">
                      <div class="dq-switch-left">
                        <div class="dq-switch-title">Auto-apply attendance overtime</div>
                        <div class="dq-help mb-0">
                          During payroll, sums <code>overtime_minutes</code> from closed
                          attendance in the pay period and pays it automatically.
                        </div>
                      </div>
                      <div class="form-check form-switch m-0">
                        <input
                          class="form-check-input dq-switch"
                          type="checkbox"
                          id="attendance_auto_apply_overtime"
                          v-model="form.attendance_auto_apply_overtime"
                        />
                      </div>
                    </div>
                  </div>

                  <div class="col-md-6">
                    <div class="dq-switch-card h-100">
                      <div class="dq-switch-left">
                        <div class="dq-switch-title">Skip auto when manual OT exists</div>
                        <div class="dq-help mb-0">
                          If HR already entered payroll overtime for the period, skip
                          automatic attendance overtime to avoid double payment.
                        </div>
                      </div>
                      <div class="form-check form-switch m-0">
                        <input
                          class="form-check-input dq-switch"
                          type="checkbox"
                          id="attendance_skip_auto_when_manual_entry"
                          v-model="form.attendance_skip_auto_when_manual_entry"
                        />
                      </div>
                    </div>
                  </div>

                  <div class="col-md-4">
                    <label class="form-label dq-label">Overtime rate type</label>
                    <select
                      v-model="form.attendance_default_overtime_id"
                      class="form-select dq-input"
                      :disabled="!form.attendance_auto_apply_overtime"
                    >
                      <option value="">Use fallback multiplier below</option>
                      <option
                        v-for="opt in overtimeTypeOptions"
                        :key="opt.id"
                        :value="String(opt.id)"
                      >
                        {{ opt.label }}
                      </option>
                    </select>
                    <div class="dq-help mt-1">
                      Pulls rate from Payroll → Overtime types.
                    </div>
                  </div>

                  <div class="col-md-4">
                    <label class="form-label dq-label">Fallback multiplier</label>
                    <input
                      v-model.number="form.attendance_default_rate_multiplier"
                      type="number"
                      min="1"
                      step="0.1"
                      class="form-control dq-input"
                      :disabled="!form.attendance_auto_apply_overtime"
                    />
                    <div class="dq-help mt-1">
                      Used when no overtime rate type is selected (e.g. 1.5x).
                    </div>
                  </div>
                </div>
              </div>
            </div>

            <div class="dq-section">
              <div class="dq-section-head">
                <div class="dq-section-title">
                  <span class="dq-section-icon dq-icon-warn">
                    <i class="ti ti-clock-hour-4"></i>
                  </span>
                  <div>
                    <div class="dq-h6">Timesheet Impact</div>
                    <div class="dq-help">
                      Optional payroll deduction from approved timesheet shortfall hours. Disabled by default.
                    </div>
                  </div>
                </div>
              </div>

              <div class="dq-section-body">
                <div class="alert alert-warning mb-3">
                  This is optional. Payroll ignores timesheet shortfalls unless you enable it below.
                </div>

                <div class="row g-3">
                  <div class="col-md-6">
                    <div class="dq-switch-card h-100">
                      <div class="dq-switch-left">
                        <div class="dq-switch-title">Deduct Unworked Timesheet Hours</div>
                        <div class="dq-help mb-0">
                          Uses only <code>Approved</code> timesheets and deducts the difference between assigned and worked hours.
                        </div>
                      </div>
                      <div class="form-check form-switch m-0">
                        <input
                          class="form-check-input dq-switch"
                          type="checkbox"
                          id="timesheet_deduct_unworked_hours"
                          v-model="form.timesheet_deduct_unworked_hours"
                        />
                      </div>
                    </div>
                  </div>

                  <div class="col-md-4">
                    <label class="form-label dq-label">Salary Basis</label>
                    <select v-model="form.timesheet_salary_basis" class="form-select dq-input">
                      <option value="basic_salary">Basic Salary</option>
                      <option value="gross_salary">Gross Salary</option>
                    </select>
                    <div class="dq-help mt-1">
                      Converted into an hourly rate using the payroll overtime monthly hours divisor.
                    </div>
                  </div>

                  <div class="col-md-6">
                    <label class="form-label dq-label">Scope by Department</label>
                    <div class="dropdown w-100">
                      <button
                        type="button"
                        class="btn btn-outline-secondary dq-btn-outline w-100 d-flex align-items-center justify-content-between"
                        data-bs-toggle="dropdown"
                      >
                        <span class="text-truncate">
                          {{
                            selectedTimesheetDepartments.length
                              ? `${selectedTimesheetDepartments.length} department(s) selected`
                              : "Select departments"
                          }}
                        </span>
                        <i class="ti ti-chevron-down"></i>
                      </button>

                      <div class="dropdown-menu w-100 p-3 dq-dropdown">
                        <input
                          v-model="ui.timesheetDepartmentQuery"
                          type="text"
                          class="form-control dq-input mb-2"
                          placeholder="Search departments..."
                          @click.stop
                        />

                        <div class="dq-multi-list">
                          <label
                            v-for="dept in filteredDepartmentOptions"
                            :key="dept.id"
                            class="dq-check-option"
                          >
                            <input
                              class="form-check-input"
                              type="checkbox"
                              :checked="form.timesheet_department_ids.includes(dept.id)"
                              @change="toggleTimesheetDepartment(dept.id)"
                            />
                            <span class="ms-2">{{ dept.name }}</span>
                          </label>

                          <div
                            v-if="!filteredDepartmentOptions.length"
                            class="small text-muted py-2"
                          >
                            No departments found.
                          </div>
                        </div>

                        <div class="d-flex justify-content-between align-items-center mt-2 pt-2 border-top">
                          <button
                            type="button"
                            class="btn btn-sm btn-light"
                            @click.stop="clearTimesheetDepartments"
                          >
                            Clear
                          </button>
                          <span class="small text-muted">
                            {{ selectedTimesheetDepartments.length }} selected
                          </span>
                        </div>
                      </div>
                    </div>
                    <div
                      v-if="selectedTimesheetDepartments.length"
                      class="dq-inline-chips mt-2"
                    >
                      <span
                        v-for="dept in selectedTimesheetDepartments"
                        :key="dept.id"
                        class="dq-chip"
                      >
                        {{ dept.name }}
                        <button
                          type="button"
                          class="dq-chip-remove"
                          title="Remove department"
                          aria-label="Remove department"
                          @click="removeTimesheetDepartment(dept.id)"
                        >
                          <i class="ti ti-x"></i>
                        </button>
                      </span>
                    </div>
                    <div class="dq-help mt-1">
                      Optional. Leave empty to apply the timesheet rule across all departments.
                    </div>
                  </div>

                  <div class="col-md-6">
                    <label class="form-label dq-label">Scope by Employee</label>
                    <div class="dropdown w-100">
                      <button
                        type="button"
                        class="btn btn-outline-secondary dq-btn-outline w-100 d-flex align-items-center justify-content-between"
                        data-bs-toggle="dropdown"
                      >
                        <span class="text-truncate">
                          {{
                            selectedTimesheetEmployees.length
                              ? `${selectedTimesheetEmployees.length} employee(s) selected`
                              : "Select employees"
                          }}
                        </span>
                        <i class="ti ti-chevron-down"></i>
                      </button>

                      <div class="dropdown-menu w-100 p-3 dq-dropdown">
                        <input
                          v-model="ui.timesheetEmployeeQuery"
                          type="text"
                          class="form-control dq-input mb-2"
                          placeholder="Search employees..."
                          @click.stop
                        />

                        <div class="dq-multi-list">
                          <label
                            v-for="emp in filteredEmployeeOptions"
                            :key="emp.id"
                            class="dq-check-option"
                          >
                            <input
                              class="form-check-input"
                              type="checkbox"
                              :checked="form.timesheet_employee_ids.includes(emp.id)"
                              @change="toggleTimesheetEmployee(emp.id)"
                            />
                            <span class="ms-2">
                              {{ emp.name }}{{ emp.code ? ` (${emp.code})` : "" }}
                            </span>
                          </label>

                          <div
                            v-if="!filteredEmployeeOptions.length"
                            class="small text-muted py-2"
                          >
                            No employees found.
                          </div>
                        </div>

                        <div class="d-flex justify-content-between align-items-center mt-2 pt-2 border-top">
                          <button
                            type="button"
                            class="btn btn-sm btn-light"
                            @click.stop="clearTimesheetEmployees"
                          >
                            Clear
                          </button>
                          <span class="small text-muted">
                            {{ selectedTimesheetEmployees.length }} selected
                          </span>
                        </div>
                      </div>
                    </div>
                    <div
                      v-if="selectedTimesheetEmployees.length"
                      class="dq-inline-chips mt-2"
                    >
                      <span
                        v-for="emp in selectedTimesheetEmployees"
                        :key="emp.id"
                        class="dq-chip"
                      >
                        {{ emp.name }}{{ emp.code ? ` (${emp.code})` : "" }}
                        <button
                          type="button"
                          class="dq-chip-remove"
                          title="Remove employee"
                          aria-label="Remove employee"
                          @click="removeTimesheetEmployee(emp.id)"
                        >
                          <i class="ti ti-x"></i>
                        </button>
                      </span>
                    </div>
                    <div class="dq-help mt-1">
                      Optional. Leave empty to apply the timesheet rule to all employees in scope.
                    </div>
                  </div>
                </div>
              </div>
            </div>

            <!-- PAYE Bands -->
            <div class="dq-section">
              <div class="dq-section-head">
                <div class="dq-section-title">
                  <span class="dq-section-icon dq-icon-blue">
                    <i class="ti ti-chart-bar"></i>
                  </span>
                  <div>
                    <div class="dq-h6">PAYE Tax Bands</div>
                    <div class="dq-help">
                      Each band is taxed at its rate. Income above the last band uses the top rate.
                    </div>
                  </div>
                </div>
              </div>

              <div class="dq-section-body">
                <div class="dq-table-wrap">
                  <div class="table-responsive">
                    <table class="table dq-table align-middle mb-0">
                      <thead>
                        <tr>
                          <th style="width: 30%">Band</th>
                          <th style="width: 40%">Upper Limit</th>
                          <th style="width: 30%">Rate</th>
                        </tr>
                      </thead>
                      <tbody>
                        <tr v-for="(band, idx) in form.paye_bands" :key="idx">
                          <td>
                            <div class="d-flex align-items-center gap-2">
                              <span class="dq-mini-badge">{{ idx + 1 }}</span>
                              Band {{ idx + 1 }}
                            </div>
                          </td>
                          <td>
                            <input
                              v-model.number="band.up_to"
                              type="number"
                              min="0"
                              class="form-control dq-input"
                              
                            />
                          </td>
                         
                          <td>
                            <div class="input-group dq-input-group">
                              <input
                                v-model.number="band.rate"
                                type="number"
                                step="0.0001"
                                min="0"
                                class="form-control dq-input"
                                
                              />
                              <span class="input-group-text dq-addon">{{ band.rate }} = {{ band.rate * 100 }}%</span>
                            </div>
                          </td>
                        </tr>

                        <tr class="dq-top-rate-row">
                          <td>
                            <div class="d-flex align-items-center gap-2">
                              <span class="dq-mini-badge dq-mini-badge-dark">
                                <i class="ti ti-arrow-up"></i>
                              </span>
                              Top Rate
                            </div>
                          </td>
                          <td class="text-muted">Above last band</td>
                          <td>
                            <div class="input-group dq-input-group">
                              <input
                                v-model.number="form.paye_top_rate"
                                type="number"
                                step="0.0001"
                                min="0"
                                class="form-control dq-input"
                                required
                              />
                              <span class="input-group-text dq-addon">0.35 = 35%</span>
                            </div>
                          </td>
                        </tr>
                      </tbody>
                    </table>
                  </div>
                </div>
              </div>
            </div>

            <div class="dq-section">
              <div class="dq-section-head">
                <div class="dq-section-title">
                  <span class="dq-section-icon dq-icon-blue">
                    <i class="ti ti-cash"></i>
                  </span>
                  <div>
                    <div class="dq-h6">Mid-Month Advance (same-month recovery)</div>
                    <div class="dq-help">
                      Short cash before month-end. Amount follows the mode below; statutory deductions are not applied on the advance run. Recovered in full on that month’s Final payroll — not for installment repayment (use Employee Loans).
                    </div>
                  </div>
                </div>
              </div>

              <div class="dq-section-body">
                <div class="alert alert-info mb-3">
                  Use <strong>Mid-Month Advance (full recovery same month)</strong> in Generate Payroll.
                  The full amount is recovered automatically on <strong>Month-End Final Payroll</strong> as a post-tax deduction.
                  For repayment over several months, create an <strong>Employee Loan</strong> instead.
                </div>

                <div class="row g-3">
                  <div class="col-md-5">
                    <label class="form-label dq-label">Advance Calculation Mode</label>
                    <select
                      v-model="form.advance_calculation_mode"
                      class="form-select dq-input"
                    >
                      <option value="percent">Same % of basic for everyone</option>
                      <option value="fixed">Fixed amount per employee only</option>
                      <option value="hybrid">Both (fixed if set, otherwise %)</option>
                    </select>
                    <div class="dq-help mt-1">
                      <template v-if="form.advance_calculation_mode === 'percent'">
                        Every employee receives the company percentage of their basic salary.
                      </template>
                      <template v-else-if="form.advance_calculation_mode === 'fixed'">
                        Set a mid-month advance amount on each employee salary record. No percentage is used.
                      </template>
                      <template v-else>
                        Use the mid-month amount on an employee salary when provided; otherwise fall back to the company %.
                      </template>
                    </div>
                  </div>

                  <div
                    v-if="form.advance_calculation_mode !== 'fixed'"
                    class="col-md-4"
                  >
                    <label class="form-label dq-label">Default Mid-Month Advance % of Basic</label>
                    <div class="input-group dq-input-group">
                      <input
                        v-model.number="form.advance_default_percent_of_basic"
                        type="number"
                        min="0"
                        max="100"
                        step="1"
                        class="form-control dq-input"
                        required
                      />
                      <span class="input-group-text dq-addon">%</span>
                    </div>
                    <div class="dq-help mt-1">
                      Used for all employees in <strong>Percent</strong> mode, or as fallback in <strong>Both</strong> mode.
                    </div>
                  </div>

                  <div
                    v-if="form.advance_calculation_mode !== 'percent'"
                    class="col-md-12"
                  >
                    <div class="alert alert-warning mb-0 py-2">
                      Enter each employee's mid-month advance on
                      <strong>Payroll → Employee Salary</strong>
                      (field: <em>Mid-Month Advance Amount</em>).
                      This is recovered in full the same month — not a multi-month loan.
                    </div>
                  </div>
                </div>
              </div>
            </div>

            <div class="dq-section">
              <div class="dq-section-head">
                <div class="dq-section-title">
                  <span class="dq-section-icon dq-icon-purple">
                    <i class="ti ti-file-invoice"></i>
                  </span>
                  <div>
                    <div class="dq-h6">Payslip Layout</div>
                    <div class="dq-help">
                      Choose how employee payslip PDFs are rendered for this company.
                    </div>
                  </div>
                </div>
              </div>

              <div class="dq-section-body">
                <div class="row g-3">
                  <div class="col-md-5">
                    <label class="form-label dq-label">PDF Orientation</label>
                    <select
                      v-model="form.payslip_orientation"
                      class="form-select dq-input"
                    >
                      <option value="portrait">Portrait (A4 vertical)</option>
                      <option value="landscape">Landscape (A4 horizontal)</option>
                    </select>
                    <div class="dq-help mt-1">
                      Applies to live payroll payslips, emailed payslips, and archive payslip downloads.
                    </div>
                  </div>
                </div>
              </div>
            </div>

            <div class="dq-section">
              <div class="dq-section-head">
                <div class="dq-section-title">
                  <span class="dq-section-icon dq-icon-blue">
                    <i class="ti ti-coin"></i>
                  </span>
                  <div>
                    <div class="dq-h6">Net Pay Rounding</div>
                    <div class="dq-help">
                      Optional payment policy only. Statutory deductions and tax stay exact to the cent.
                    </div>
                  </div>
                </div>
              </div>

              <div class="dq-section-body">
                <div class="row g-3">
                  <div class="col-md-6">
                    <label class="form-label dq-label">Final net payable</label>
                    <select
                      v-model="form.net_pay_rounding_mode"
                      class="form-select dq-input"
                    >
                      <option value="none">Keep cents (default)</option>
                      <option value="nearest_shilling">Round to nearest shilling (half up)</option>
                    </select>
                    <div class="dq-help mt-1">
                      Example: 33,770.44 becomes 33,770.00; 12,345.50 becomes 12,346.00.
                      Payslips show the rounding delta when applied. Re-run payroll after changing this.
                    </div>
                  </div>
                </div>
              </div>
            </div>

            <div class="dq-section">
              <div class="dq-section-head">
                <div class="dq-section-title">
                  <span class="dq-section-icon dq-icon-green">
                    <i class="ti ti-checkup-list"></i>
                  </span>
                  <div>
                    <div class="dq-h6">Approval Workflow</div>
                    <div class="dq-help">
                      Choose whether payroll must be approved by a different user after it is run.
                    </div>
                  </div>
                </div>
              </div>

              <div class="dq-section-body">
                <div class="alert alert-info mb-3">
                  Disable this only if the company wants Tenant Admins to run payroll and approve it themselves without a separate checker.
                </div>

                <div class="row g-3">
                  <div class="col-md-8">
                    <div class="dq-switch-card h-100">
                      <div class="dq-switch-left">
                        <div class="dq-switch-title">Require Separate Approver</div>
                        <div class="dq-help mb-0">
                          When enabled, the user who ran payroll cannot approve the same run. When disabled, Tenant Admins can run and approve payroll themselves.
                        </div>
                      </div>
                      <div class="form-check form-switch m-0">
                        <input
                          class="form-check-input dq-switch"
                          type="checkbox"
                          id="approval_require_separate_approver"
                          v-model="form.approval_require_separate_approver"
                        />
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            </div>

            <!-- Sticky save bar -->
            <div class="dq-sticky-save">
              <div class="dq-sticky-left">
                <div class="dq-sticky-title">
                  <i class="ti ti-device-floppy me-2"></i>
                  Save changes
                </div>
                <div class="dq-help mb-0">
                  Changes apply to payroll computations after saving.
                </div>
              </div>

              <button type="submit" class="btn btn-primary dq-btn-primary" :disabled="loading" v-can="'Payroll.Edit'">
                <span v-if="loading" class="spinner-border spinner-border-sm me-2"></span>
                Save Configuration
                <i class="ti ti-arrow-right ms-2"></i>
              </button>
            </div>
          </form>
        </div>
      </div>
    </div>

    <div class="footer d-sm-flex align-items-center justify-content-between border-top bg-white p-3">
      <p class="mb-0">2014 - {{ new Date().getFullYear() }} &copy; DoneQuick HR & Payroll</p>
      <p class="mb-0">
        Powered by <a href="#" @click.prevent class="text-primary">DoneQuick Technological World</a>
      </p>
    </div>
  </div>
</template>

<style scoped>
/* ===== Payroll Settings: World-class UI (no logic) ===== */
.dq-payroll-settings .dq-card {
  border: 0;
  border-radius: 18px;
  overflow: visible;
  box-shadow: 0 22px 60px rgba(16, 24, 40, 0.10);
}
.dq-card-header {
  padding: 16px 18px;
  background: linear-gradient(180deg, rgba(248, 250, 252, 1), rgba(255, 255, 255, 1));
  border-bottom: 1px solid rgba(0, 0, 0, 0.06);
}
.dq-card-body {
  padding: 16px 18px;
  overflow: visible;
}

.dq-pill {
  display: inline-flex;
  align-items: center;
  gap: 6px;
  padding: 6px 10px;
  border-radius: 999px;
  background: rgba(2, 132, 199, 0.08);
  border: 1px solid rgba(2, 132, 199, 0.12);
  color: rgba(17, 24, 39, 0.75);
  font-size: 0.82rem;
  font-weight: 700;
}
.dq-pill-soft {
  background: rgba(99, 102, 241, 0.10);
  border-color: rgba(99, 102, 241, 0.14);
}

.dq-icon-badge {
  width: 44px;
  height: 44px;
  border-radius: 14px;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  font-size: 20px;
}
.dq-icon-blue { background: rgba(59, 130, 246, 0.12); color: rgba(37, 99, 235, 1); }
.dq-icon-green { background: rgba(16, 185, 129, 0.12); color: rgba(5, 150, 105, 1); }
.dq-icon-purple { background: rgba(99, 102, 241, 0.12); color: rgba(79, 70, 229, 1); }
.dq-icon-warn { background: rgba(245, 158, 11, 0.12); color: rgba(217, 119, 6, 1); }

.dq-status {
  display: inline-flex;
  align-items: center;
  padding: 8px 12px;
  border-radius: 14px;
  background: rgba(148, 163, 184, 0.14);
  border: 1px solid rgba(148, 163, 184, 0.18);
  font-weight: 800;
  color: rgba(17, 24, 39, 0.7);
}

.dq-hero {
  border: 0;
  border-radius: 18px;
  overflow: hidden;
  box-shadow: 0 18px 50px rgba(16, 24, 40, 0.08);
}
.dq-hero .card-body {
  padding: 18px;
  background:
    radial-gradient(1200px 400px at 10% 10%, rgba(59, 130, 246, 0.10), transparent 60%),
    radial-gradient(1000px 400px at 90% 20%, rgba(99, 102, 241, 0.10), transparent 55%),
    linear-gradient(180deg, rgba(248, 250, 252, 1), rgba(255, 255, 255, 1));
  border: 1px solid rgba(0, 0, 0, 0.06);
}
.dq-hero-grid {
  display: grid;
  grid-template-columns: 1.2fr 0.8fr;
  gap: 16px;
  align-items: center;
}
@media (max-width: 992px) {
  .dq-hero-grid { grid-template-columns: 1fr; }
}

.dq-hero-left { display: flex; align-items: center; gap: 12px; }
.dq-hero-badge {
  width: 54px; height: 54px;
  border-radius: 18px;
  background: rgba(37, 99, 235, 0.12);
  color: rgba(37, 99, 235, 1);
  display: inline-flex;
  align-items: center;
  justify-content: center;
  font-size: 22px;
  border: 1px solid rgba(37, 99, 235, 0.14);
}
.dq-hero-title { font-weight: 950; letter-spacing: -0.02em; }
.dq-hero-sub { color: rgba(17, 24, 39, 0.65); }

.dq-hero-right {
  display: grid;
  grid-template-columns: 1fr;
  gap: 10px;
}
.dq-meta {
  padding: 10px 12px;
  border-radius: 14px;
  background: rgba(148, 163, 184, 0.10);
  border: 1px solid rgba(0, 0, 0, 0.06);
}
.dq-meta-label { font-size: 0.78rem; color: rgba(17, 24, 39, 0.55); font-weight: 800; }
.dq-meta-value { font-weight: 900; color: rgba(17, 24, 39, 0.85); }

.dq-hero-hint {
  display: flex;
  align-items: center;
  padding: 10px 12px;
  border-radius: 14px;
  background: rgba(2, 132, 199, 0.08);
  border: 1px solid rgba(2, 132, 199, 0.12);
  color: rgba(17, 24, 39, 0.65);
  font-size: 0.84rem;
  font-weight: 750;
}

/* Sections */
.dq-section {
  position: relative;
  z-index: 1;
  border-radius: 18px;
  border: 1px solid rgba(0, 0, 0, 0.06);
  background: #fff;
  overflow: visible;
  margin-bottom: 14px;
}
.dq-section:has(.dropdown.show) {
  z-index: 40;
}
.dq-section-head {
  padding: 12px 14px;
  background: rgba(248, 250, 252, 1);
  border-bottom: 1px solid rgba(0, 0, 0, 0.06);
}
.dq-section-title {
  display: flex;
  align-items: center;
  gap: 12px;
}
.dq-section-icon {
  width: 40px; height: 40px;
  border-radius: 14px;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  border: 1px solid rgba(0,0,0,0.04);
}
.dq-section-body { padding: 14px; }
.dq-h6 { font-weight: 950; color: rgba(17, 24, 39, 0.9); }
.dq-help { font-size: 0.82rem; color: rgba(17, 24, 39, 0.6); margin-top: 2px; }

.dq-label { font-weight: 850; color: rgba(17, 24, 39, 0.85); }

/* Inputs */
.dq-input {
  border-radius: 14px !important;
  border: 1px solid rgba(0, 0, 0, 0.08) !important;
  min-height: 44px;
  transition: box-shadow 0.12s ease, border-color 0.12s ease;
}
.dq-input:focus {
  border-color: rgba(59, 130, 246, 0.45) !important;
  box-shadow: 0 0 0 4px rgba(59, 130, 246, 0.12) !important;
}
.dq-input-group .dq-addon {
  border-radius: 14px !important;
  border: 1px solid rgba(0,0,0,0.08) !important;
  background: rgba(148, 163, 184, 0.10);
  color: rgba(17, 24, 39, 0.65);
  font-weight: 750;
}

/* Switch card */
.dq-switch-card {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 12px;
  padding: 12px;
  border-radius: 16px;
  background: rgba(16, 185, 129, 0.07);
  border: 1px solid rgba(16, 185, 129, 0.12);
  margin-top: 6px;
}
.dq-switch-title { font-weight: 950; color: rgba(17, 24, 39, 0.85); }
.dq-switch { cursor: pointer; }

/* Dropdown */
.dq-btn-outline {
  border-radius: 14px;
  min-height: 44px;
}
.dq-dropdown {
  border-radius: 14px;
  border: 1px solid rgba(0,0,0,0.06);
  box-shadow: 0 18px 40px rgba(16, 24, 40, 0.12);
  padding: 6px;
  z-index: 1050;
}
.dq-dropdown-item {
  border-radius: 12px;
  padding: 10px 10px;
  display: flex;
  align-items: center;
  gap: 10px;
}
.dq-dropdown-item:hover { background: rgba(59, 130, 246, 0.08); }
.dq-dot {
  width: 8px; height: 8px;
  border-radius: 999px;
  background: rgba(59, 130, 246, 0.8);
}
.dq-multi-list {
  max-height: 220px;
  overflow: auto;
  padding-right: 4px;
}
.dq-check-option {
  display: flex;
  align-items: center;
  gap: 4px;
  padding: 9px 10px;
  border-radius: 12px;
  cursor: pointer;
  transition: background 0.12s ease;
}
.dq-check-option:hover {
  background: rgba(59, 130, 246, 0.08);
}
.dq-inline-chips {
  display: flex;
  flex-wrap: wrap;
  gap: 8px;
}
.dq-chip {
  display: inline-flex;
  align-items: center;
  gap: 4px;
  padding: 6px 8px 6px 10px;
  border-radius: 999px;
  background: rgba(37, 99, 235, 0.08);
  color: rgba(30, 64, 175, 0.92);
  border: 1px solid rgba(37, 99, 235, 0.12);
  font-size: 0.82rem;
  font-weight: 700;
}
.dq-chip-remove {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  width: 18px;
  height: 18px;
  padding: 0;
  border: 0;
  border-radius: 999px;
  background: transparent;
  color: rgba(30, 64, 175, 0.75);
  line-height: 1;
  cursor: pointer;
  transition: background 0.12s ease, color 0.12s ease;
}
.dq-chip-remove:hover {
  background: rgba(37, 99, 235, 0.16);
  color: rgba(30, 64, 175, 1);
}
.dq-chip-remove .ti {
  font-size: 0.75rem;
}

/* Table */
.dq-table-wrap {
  border-radius: 16px;
  overflow: hidden;
  border: 1px solid rgba(0,0,0,0.06);
}
.dq-table thead th {
  background: rgba(248, 250, 252, 1);
  color: rgba(17, 24, 39, 0.75);
  font-weight: 950;
  border-bottom: 1px solid rgba(0,0,0,0.06);
}
.dq-table tbody td { vertical-align: middle; }
.dq-mini-badge {
  width: 26px; height: 26px;
  border-radius: 10px;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  font-weight: 950;
  background: rgba(59, 130, 246, 0.12);
  color: rgba(37, 99, 235, 1);
  border: 1px solid rgba(59, 130, 246, 0.14);
}
.dq-mini-badge-dark {
  background: rgba(17, 24, 39, 0.08);
  color: rgba(17, 24, 39, 0.85);
  border-color: rgba(17, 24, 39, 0.10);
}
.dq-top-rate-row td {
  background: rgba(245, 158, 11, 0.06);
}

/* Sticky save bar */
.dq-sticky-save {
  position: sticky;
  bottom: 12px;
  z-index: 2;
  margin-top: 16px;
  padding: 12px 14px;
  border-radius: 18px;
  background: rgba(255, 255, 255, 0.92);
  backdrop-filter: blur(10px);
  border: 1px solid rgba(0,0,0,0.08);
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 12px;
}
.dq-sticky-title {
  font-weight: 950;
  color: rgba(17, 24, 39, 0.88);
  display: flex;
  align-items: center;
}
.dq-btn-primary {
  border-radius: 14px;
  padding: 10px 14px;
  box-shadow: 0 14px 26px rgba(37, 99, 235, 0.18);
}

/* Small screens spacing */
@media (max-width: 576px) {
  .dq-card-body { padding: 14px; }
  .dq-section-body { padding: 12px; }
  .dq-sticky-save { flex-direction: column; align-items: stretch; }
}
</style>


